import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Package, Scan, ArrowRight } from "lucide-react";
import { useState } from "react";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";
import { MetefyQrAuthDrawer } from "@/components/auth/MetefyQrAuthDrawer";

interface AboutCtaSectionProps {
  openDemoProfile?: (profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    x_username: string | null;
    bio: string | null;
    metro_card_number: string | null;
    created_at: string;
    updated_at: string;
    show_email: boolean;
    show_mobile: boolean;
    metefy_token: string | null;
    photo_url?: string | null;
    mask_bio?: boolean;
    social_sms?: string | null;
    social_call?: string | null;
    social_telegram?: string | null;
    social_whatsapp?: string | null;
    use_social_icons?: boolean;
  }) => void;
}

export function AboutCtaSection({ openDemoProfile }: AboutCtaSectionProps = {}) {
  const { user } = useAuth();
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const [isAuthDrawerOpen, setIsAuthDrawerOpen] = useState(false);

  // Generate Action Schema for SEO
  const actionSchema = {
    "@context": "https://schema.org",
    "@type": "Action",
    "name": "Get Started with Metefy",
    "description": "Join Metefy's digital identity platform to protect your belongings and enable quick item recovery",
    "potentialAction": [
      {
        "@type": "BuyAction",
        "name": "Get QR Stickers",
        "description": "Purchase Metefy QR stickers to start protecting your items",
        "target": "https://metefy.in",
        "price": "179",
        "priceCurrency": "INR"
      },
      {
        "@type": "Action",
        "name": user ? "Profile" : "Get Started",
        "description": user ? "Access your Metefy profile to manage your items" : "Create a free account to link your metro card and begin using Metefy services",
        "target": user ? "https://metefy.com/profile" : "https://metefy.com/auth"
      }
    ],
    "object": {
      "@type": "Product",
      "name": "Metefy Digital Identity Platform",
      "description": "QR-based item recovery and digital identity solution",
      "offers": {
        "@type": "Offer",
        "price": "179",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "url": "https://metefy.in",
        "priceValidUntil": "2026-12-31"
      }
    }
  };

  return (
    <section className="py-24 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Ready to Protect Your <span className="text-primary">Belongings</span>?
          </h2>
          <p className="text-xl text-foreground/80 mb-8 max-w-2xl mx-auto">
            Join our community and never worry about losing your valuables. Get started in just 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsStickerDrawerOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2"
            >
              <Package className="w-5 h-5" />
              Get QR Stickers
              <ArrowRight className="w-4 h-4" />
            </button>
            {user ? (
              <Link 
                to="/profile"
                className="bg-foreground hover:bg-foreground/90 text-background px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer"
                aria-label="Go to your profile"
              >
                <Scan className="w-5 h-5 group-hover:scale-110 transition-transform" aria-hidden="true" />
                Profile
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            ) : (
              <button
                onClick={() => setIsAuthDrawerOpen(true)}
                className="bg-foreground hover:bg-foreground/90 text-background px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer"
                aria-label="Sign in or create an account to get started"
              >
                <Scan className="w-5 h-5 group-hover:scale-110 transition-transform" aria-hidden="true" />
                Get Started
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />

      {/* Inject Action Schema for SEO - hidden from users */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(actionSchema) }}
        aria-hidden="true"
      ></script>
      
      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
      <MetefyQrAuthDrawer isOpen={isAuthDrawerOpen} setIsOpen={setIsAuthDrawerOpen} />
    </section>
  );
} 